/**
 * Keeps one account's browser-local data from leaking into the next account (or guest) that uses
 * the same browser.
 *
 * The TanStack user-state cache (`query/user-state/userStatePersister.ts`) and redux-persist's
 * `persist:root` (profile draft `jobForm`, `comparisonPreferences`, `recentSearches`, ...) both
 * live in IndexedDB under fixed keys with no owner. Before this existed, signing out left the
 * previous account's saved/viewed/applied jobs and profile in place: the next guest saw them, and
 * the next account to sign in could have them uploaded into it (the "remote empty, local not"
 * anonymous sync in `useUserStateQuery.ts`, and the profile debounce-sync in
 * `useProfilePreferencesSettingsSync.ts`).
 *
 * The server stamps an opaque owner id into the readable `jc_owner` cookie at sign-in and clears it
 * at sign-out (`server/auth/local-data-owner-cookie.ts`). Here the client compares that with the
 * owner recorded next to the local data, and wipes the per-account stores when a signed-in
 * account's data would otherwise be seen by someone else. Every read/write of those stores awaits
 * `ensureLocalDataOwnerReconciled()` first, so nothing can restore the stale data before the wipe.
 */
import { loadLocalForage } from './get-localforage-instance';

export const LOCAL_DATA_OWNER_COOKIE_NAME = 'jc_owner';

/** Recorded owner of the browser-local data: an owner id, or `GUEST_OWNER`. */
const LOCAL_DATA_OWNER_STORAGE_KEY = 'jc_local_data_owner';
export const GUEST_OWNER = 'guest';

/** IndexedDB (localforage) keys holding per-account data. */
const PER_ACCOUNT_LOCALFORAGE_KEYS = ['tanstack-query-user-state-cache', 'persist:root'];
/** localStorage keys holding per-account data. */
const PER_ACCOUNT_LOCAL_STORAGE_KEYS = ['appliedJobs', 'jc.savedSearches.deletedTombstones'];
/** Cookies mirroring per-account data for SSR (see utils/shared/client-preferences.ts). */
const PER_ACCOUNT_COOKIES = ['matchProfile', 'savedJobs', 'appliedJobs', 'viewedJobs'];

/**
 * - Same owner: keep.
 * - No recorded owner (data from before this existed): keep, there is nothing to compare against.
 * - Guest data and someone signs in: keep, so jobs saved before signing up carry over.
 * - An account's data and a different account or a guest is now here: wipe.
 */
export function decideLocalDataOwnerAction(storedOwner: string | null, currentOwner: string): 'keep' | 'wipe' {
  if (!storedOwner || storedOwner === currentOwner || storedOwner === GUEST_OWNER) return 'keep';
  return 'wipe';
}

export function readCurrentLocalDataOwner(cookieString: string): string {
  const match = cookieString.match(new RegExp(`(?:^|;\\s*)${LOCAL_DATA_OWNER_COOKIE_NAME}=([^;]+)`));
  const value = match ? decodeURIComponent(match[1]).trim() : '';
  return value || GUEST_OWNER;
}

async function reconcileLocalDataOwner(): Promise<void> {
  const currentOwner = readCurrentLocalDataOwner(document.cookie);
  let storedOwner: string | null;
  try {
    storedOwner = window.localStorage.getItem(LOCAL_DATA_OWNER_STORAGE_KEY);
  } catch {
    return; // No localStorage (blocked storage): nothing reliable to compare against.
  }

  if (decideLocalDataOwnerAction(storedOwner, currentOwner) === 'wipe') {
    for (const key of PER_ACCOUNT_LOCAL_STORAGE_KEYS) {
      try {
        window.localStorage.removeItem(key);
      } catch {}
    }
    for (const name of PER_ACCOUNT_COOKIES) {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
    }
    const storage = await loadLocalForage();
    if (storage) {
      await Promise.all(PER_ACCOUNT_LOCALFORAGE_KEYS.map((key) => storage.removeItem(key)));
    }
  }

  // Recorded only after a successful wipe, so a failed one is retried on the next page load.
  try {
    window.localStorage.setItem(LOCAL_DATA_OWNER_STORAGE_KEY, currentOwner);
  } catch {}
}

let reconcilePromise: Promise<void> | null = null;

/**
 * Resolves once the browser-local data is known to belong to whoever is on this page. Runs once
 * per page load (sign-in and sign-out are always full navigations).
 */
export function ensureLocalDataOwnerReconciled(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (!reconcilePromise) {
    reconcilePromise = reconcileLocalDataOwner().catch((error) => {
      console.warn('[local-data-owner] Failed to reconcile local data owner:', error);
    });
  }
  return reconcilePromise;
}

/** Test-only: forget the per-page-load result. */
export function resetLocalDataOwnerReconcileForTests(): void {
  reconcilePromise = null;
}
